import { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  fetchCurrentUser,
  getAuthToken,
  logout,
  setAuthToken,
} from "@/lib/mapioApi";
import { signOutSupabase, supabase } from "@/lib/supabase";

const AuthContext = createContext({
  user: null,
  profile: null,
  loading: true,
  signOut: async () => {},
  refreshProfile: async () => {},
  setAuthSession: () => {},
});

export const useAuth = () => useContext(AuthContext);

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyAuthData = (data) => {
    setUser(data?.user || null);
    setProfile(data?.profile || data?.user || null);
  };

  const applySupabaseSession = useCallback((session) => {
    const supabaseUser = session?.user;
    if (!supabaseUser) return;

    const metadata = supabaseUser.user_metadata || {};
    const fallbackUser = {
      id: supabaseUser.id,
      email: supabaseUser.email,
      username:
        metadata.full_name ||
        metadata.name ||
        metadata.preferred_username ||
        supabaseUser.email?.split("@")[0] ||
        "Mapix user",
      avatar_url: metadata.avatar_url || metadata.picture || null,
      mo_balance: 0,
    };

    applyAuthData({ user: fallbackUser, profile: fallbackUser });
  }, []);

  useEffect(() => {
    const loadSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token || getAuthToken();
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        setAuthToken(token);
        if (session) applySupabaseSession(session);
        const data = await fetchCurrentUser();
        applyAuthData(data);
      } catch (err) {
        console.error("Session restore failed:", err);
        if (!session) {
          setAuthToken(null);
          applyAuthData(null);
        }
      } finally {
        setLoading(false);
      }
    };

    loadSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!session?.access_token) return;
      setAuthToken(session.access_token);
      applySupabaseSession(session);
      try {
        const data = await fetchCurrentUser();
        applyAuthData(data);
      } catch (err) {
        console.error("Google session sync failed:", err);
      }
    });

    return () => subscription.unsubscribe();
  }, [applySupabaseSession]);

  const setAuthSession = (data) => {
    applyAuthData(data);
  };

  const signOut = async () => {
    applyAuthData(null);
    try {
      await signOutSupabase();
      await logout();
    } catch (err) {
      console.error("Sign out error:", err);
    }
  };

  const refreshProfile = async () => {
    if (!getAuthToken()) return;
    const data = await fetchCurrentUser();
    applyAuthData(data);
    return data.profile;
  };

  return (
    <AuthContext.Provider
      value={{ user, profile, loading, signOut, refreshProfile, setAuthSession }}
    >
      {children}
    </AuthContext.Provider>
  );
}
